<template>
  <div class="documentDetails">
    <div v-if="loading || !document">
      <b-container fluid="xxl">
        <b-row class="justify-content-md-center">
            <b-col class="mt-5">
              <div class="spinner-border m-5" role="status">
                <span class="sr-only">Loading...</span>
              </div>
            </b-col>
        </b-row>
      </b-container>
    </div>
    <div v-else>
      <b-container fluid="xxl">
        <b-row>
          <b-col class="pl-0">
            <PageTitle v-if="document" v-bind:title="document.edition.sigleLegam" :pretitle="this.$t('DocLingTextDetails')" />
          </b-col>
          <b-col>
            <div class="mt-5 text-right">
              <button @click="openDownloadXml" class="btn-legam-xs mb-2">
                <b-icon icon="file-binary-fill"></b-icon>{{ $t('DownloadXml') }}
              </button>
              <button v-if="!config.staticData" @click="openDownloadPdf" class="btn-legam-xs mb-2">
                <b-icon icon="file-binary-fill"></b-icon>{{ $t('DownloadPdf') }}
              </button>
              <button
                v-else
                class="btn-legam-xs mb-2"
                disabled
                title="PDF/LaTeX generation is not included in static exports"
              >
                <b-icon icon="file-binary-fill"></b-icon>{{ $t('DownloadPdf') }}
              </button>
              <span v-if="editMode && config.editable">
                <button @click="saveEdit" class="btn-legam-xs mb-2 btn-primary">
                  <b-icon icon="lock-fill"></b-icon>
                  Save changes
                </button>
                <button @click="cancelEdit" class="btn-legam-xs mb-2">
                  Cancel
                </button>
                <button v-if="$store.getters.checkRole('legam.doc.document.delete')" @click="deleteItem" class="btn-legam-xs mb-2 btn-danger">
                  <b-icon icon="trash2-fill"></b-icon>
                  Delete
                </button>
              </span>
              <span v-else>
                <button @click="unlockEdit" v-if="$store.getters.checkRole('legam.doc.document.modify')" class="btn-legam-xs mb-2">
                  <b-icon icon="unlock-fill"></b-icon>
                  Unlock editing
                </button>
                <button v-if="!$store.getters.checkRole('legam.doc.document.modify') && $store.getters.checkRole('legam.doc.document.delete')" @click="deleteItem" class="btn-legam-xs mb-2 btn-danger">
                  <b-icon icon="trash2-fill"></b-icon>
                  Delete
                </button>
              </span>
            </div>
          </b-col>
        </b-row>
        <b-row class="justify-content-md-center">
          <b-col>
            <article>
              <b-row>
                <b-col style="position: relative">
                  <b-row>
                    <b-col cols="12">
                      <b-row>
                        <b-col cols="4">
                          <div class="legam-details-box">
                            <b-row>
                              <b-col>
                                <h6>{{ $t('SigleEdition') }}</h6>
                                <EditionSelect v-if="editMode" :edition="document.edition" @select="selectEdition" />
                                <span v-else>
                                  <router-link
                                    v-if="document.editionId"
                                    :to="{name: 'EditionDetails', params: {
                                      slug: document.edition.sigleLegamNormalized,
                                      id: document.edition.id
                                    }}"
                                    v-html="document.edition.sigleLegam"
                                  ></router-link>
                                  <font style="color:gray">  [{{ document.edition.liability }}]</font>
                                </span>
                              </b-col>
                            </b-row>
                            <b-row class="mt-3">
                              <b-col>
                                <h6>Alternative Sigle<span v-if="editMode"> (change on edition)</span></h6>
                                <span v-if="document.corpusId && document.corpus.editionId">
                                  <span
                                    v-for="sigle in document.edition.siglesources"
                                    :key="sigle.id"
                                    v-html="`${sigle.alternativeSigle} - ${sigle.siglesourceCode}`"
                                  ></span>
                                </span>
                              </b-col>
                            </b-row>
                            <b-row class="mt-3">
                              <b-col>
                                <h6>{{ $t('Corpus') }}<span v-if="editMode"> (change on edition)</span></h6>
                                <span v-if="document.corpusId && document.corpus.editionId">
                                  <router-link
                                    v-if="document.corpus.editionId"
                                    :to="{name: 'EditionDetails', params: {
                                      slug: document.corpus.edition.sigleLegamNormalized,
                                      id: document.corpus.edition.id
                                    }}"
                                    v-html="document.corpus.edition.sigleLegam"
                                  ></router-link>
                                </span>
                              </b-col>
                            </b-row>
                            <b-row class="mt-3">
                              <b-col>
                                <h6>{{ $t('Transcription') }}<span v-if="editMode"> (change on edition)</span></h6>
                                <span v-if="document.edition.transcription" v-html="document.edition.transcription"></span>
                                <span v-else v-html="document.corpus.edition.transcription"></span>
                              </b-col>
                            </b-row>
                            <b-row class="mt-3">
                              <b-col>
                                <h6>{{ $t('Responsibility') }}<span v-if="editMode"> (change on edition)</span></h6>
                                <span v-if="document.edition.responsibility" v-html="document.edition.responsibility"></span>
                                <span v-else v-html="document.corpus.edition.responsibility"></span>
                              </b-col>
                            </b-row>
                          </div>
                          <div class="legam-details-box mt-3">
                            <b-row>
                              <b-col>
                                <h6>{{ $t('Date') }}<span v-if="editMode"> (change on manuscript)</span></h6>
                                <span v-if="document.manuscript" v-html="document.manuscript.dateSpecPhil"></span>
                              </b-col>
                            </b-row>
                            <b-row class="mt-3">
                              <b-col>
                                <h6>{{ $t('CommentDates') }}<span v-if="editMode"> (change on manuscript)</span></h6>
                                <span v-if="document.manuscript" v-html="document.manuscript.commentDate"></span>
                              </b-col>
                            </b-row>
                            <b-row class="mt-3">
                              <b-col>
                                <h6>{{ $t('DateNum') }}<span v-if="editMode"> (change on manuscript)</span></h6>
                                <span v-if="document.manuscript" v-html="document.manuscript.dateNum"></span>
                              </b-col>
                            </b-row>
                            <b-row class="mt-3">
                              <b-col>
                                <h6>{{ $t('Scripta') }}<span v-if="editMode"> (change on manuscript)</span></h6>
                                <span v-if="document.manuscript && document.manuscript.localization" v-html="document.manuscript.localization.scriptas.map(scripta => scripta.code).join(', ')" />
                              </b-col>
                            </b-row>
                            <b-row class="mt-3">
                              <b-col>
                                <h6>{{ $t('Location') }}<span v-if="editMode"> (change on manuscript)</span></h6>
                                <span v-if="document.manuscript && document.manuscript.localization" v-html="document.manuscript.localization.locations.map(location => location.display).join(', ')"></span>
                              </b-col>
                            </b-row>
                            <b-row class="mt-3">
                              <b-col>
                                <h6>{{ $t('Genre') }}<span v-if="editMode"> (change on text)</span></h6>
                                <span v-if="document.editionId" v-html="document.text.genre ? document.text.genre.display : ''"></span>
                              </b-col>
                            </b-row>
                          </div>
                        </b-col>
                        <b-col cols="4">
                          <b-row class="mt-4-8">
                            <b-col>&nbsp;</b-col>
                          </b-row>
                          <div class="legam-details-box mt-3">
                            <b-row>
                              <b-col>
                                <h6>{{ $t('Authorized') }}</h6>
                                <b-form-input
                                  id="authorized"
                                  v-model="document.authorized"
                                  placeholder="Authorized"
                                  v-if="editMode"
                                ></b-form-input>
                                <span v-else v-html="document.authorized"></span>
                              </b-col>
                            </b-row>
                            <b-row class="mt-3">
                              <b-col>
                                <h6>{{ $t('Arranger') }}</h6>
                                <b-form-input
                                  id="arranger"
                                  v-model="document.arranger"
                                  placeholder="Arranger"
                                  v-if="editMode"
                                ></b-form-input>
                                <span v-else v-html="document.arranger"></span>
                              </b-col>
                            </b-row>
                            <b-row class="mt-3">
                              <b-col>
                                <h6>{{ $t('SealProvider') }}</h6>
                                <b-form-input
                                  id="sealProvider"
                                  v-model="document.sealProvider"
                                  placeholder="Arranger"
                                  v-if="editMode"
                                ></b-form-input>
                                <span v-else v-html="document.sealProvider"></span>
                              </b-col>
                            </b-row>
                            <b-row class="mt-3">
                              <b-col>
                                <h6>{{ $t('Beneficiary') }}</h6>
                                <b-form-input
                                  id="beneficiary"
                                  v-model="document.beneficiary"
                                  placeholder="Beneficiary"
                                  v-if="editMode"
                                ></b-form-input>
                                <span v-else v-html="document.beneficiary"></span>
                              </b-col>
                            </b-row>
                            <b-row class="mt-3">
                              <b-col>
                                <h6>{{ $t('Agent') }}</h6>
                                <b-form-input
                                  id="agent"
                                  v-model="document.agent"
                                  placeholder="Agent"
                                  v-if="editMode"
                                ></b-form-input>
                                <span v-else v-html="document.agent"></span>
                              </b-col>
                            </b-row>
                            <b-row class="mt-3">
                              <b-col>
                                <h6>{{ $t('PlaceOfIssue') }}<span v-if="editMode"> (change on text)</span></h6>
                                <span v-if="document.textId" v-html="document.text.placeOfIssue"></span>
                              </b-col>
                            </b-row>
                            <b-row class="mt-3">
                              <b-col>
                                <h6>{{ $t('Scriptoria') }}<span v-if="editMode"> (change on manuscript > localization > scriptorium)</span></h6>
                                <div
                                  v-if="document.manuscript && document.manuscript.localization && document.manuscript.localization.scriptoriums"
                                >
                                  <ul style="list-style-type:none;list-style-position: outside;">
                                    <li
                                      v-for="scr in document.manuscript.localization.scriptoriums"
                                      :key="scr.id"
                                      style="margin-left: -40px;"
                                    >
                                      <router-link
                                        :to="{name: 'ScriptoriumDetails', params: {
                                          id: scr.id
                                        }}"
                                        v-html="scriptoriumString(scr)"
                                      ></router-link>
                                    </li>
                                  </ul>
                                </div>
                              </b-col>
                            </b-row>
                            <b-row class="mt-3">
                              <b-col>
                                <h6>{{ $t('CommentScriptorium') }}<span v-if="editMode"> (change on manuscript > localization)</span></h6>
                                <span
                                  v-if="document.manuscript && document.manuscript.localization"
                                  v-html="document.manuscript.localization.commentScriptorium"
                                ></span>
                              </b-col>
                            </b-row>
                            <b-row class="mt-3">
                              <b-col>
                                <h6>{{ $t('Scribe') }}<span v-if="editMode"> (change on manuscript > personhist > scribe)</span></h6>
                                <span
                                  v-if="document.manuscript && document.manuscript.personhists"
                                  v-html="document.manuscript.personhists.filter(personhist => personhist.relationType == 'scribe').map(personhist => personhist.fullName).join(', ')"
                                ></span>
                              </b-col>
                            </b-row>
                          </div>
                        </b-col>
                        <b-col cols="4">
                          <b-row>
                            <b-col>
                              <h6>{{ $t('Text') }}</h6>
                              <TextSelect v-if="editMode" :text="document.text" @select="selectText" />
                              <span v-else>
                                <router-link
                                  v-if="document.textId"
                                  :to="{name: 'TextDetails', params: {
                                    slug: document.text.sigleLegamNormalized,
                                    id: document.text.id
                                  }}"
                                  v-html="document.text.sigleLegam"
                                ></router-link>
                              </span>
                            </b-col>
                          </b-row>
                          <div class="legam-details-box mt-3">
                            <b-row>
                              <b-col>
                                <h6>{{ $t('ManuscriptCode') }}</h6>
                                <ManuscriptSelect v-if="editMode" :manuscript="document.manuscript" @select="selectManuscript" />
                                <span v-else>
                                  <router-link
                                    v-if="document.manuscript"
                                    :to="{name: 'ManuscriptDetails', params: {
                                      slug: document.manuscript.codeRepcritNormalized,
                                      id: document.manuscript.id
                                    }}"
                                    v-html="document.manuscript.codeRepcrit"
                                  ></router-link>
                                  <font v-if="document.manuscript.transmissionMode" style="color:gray">  [{{ document.manuscript.transmissionMode }}]</font>
                                </span>
                              </b-col>
                            </b-row>
                            <b-row class="mt-3">
                              <b-col>
                                <h6>{{ $t('FullCode') }}<span v-if="editMode"> (change on manuscript)</span></h6>
                                <span
                                  v-if="document.manuscript"
                                  v-html="document.manuscript.codeFull"
                                ></span>
                              </b-col>
                            </b-row>
                            <b-row class="mt-3">
                              <b-col>
                                <h6>{{ $t('Materials') }}<span v-if="editMode"> (change on manuscript)</span></h6>
                                <span
                                  v-if="document.manuscript"
                                  v-html="document.manuscript.materials"
                                ></span>
                              </b-col>
                            </b-row>
                            <b-row class="mt-3">
                              <b-col>
                                <h6>{{ $t('FormerEdition') }}<span v-if="editMode"> (change on edition)</span></h6>
                                <span
                                  v-if="document.edition"
                                  v-html="document.edition.formerEdition"
                                ></span>
                              </b-col>
                            </b-row>
                            <b-row class="mt-3">
                              <b-col>
                                <h6>{{ $t('FormerDescription') }}<span v-if="editMode"> (change on edition)</span></h6>
                                <span
                                  v-if="document.edition"
                                  v-html="document.edition.formerDescription"
                                ></span>
                              </b-col>
                            </b-row>
                            <b-row class="mt-3">
                              <b-col>
                                <h6>{{ $t('CommentWriting') }}<span v-if="editMode"> (change on manuscript)</span></h6>
                                <span
                                  v-if="document.manuscript"
                                  v-html="document.manuscript.commentWriting"
                                ></span>
                              </b-col>
                            </b-row>
                            <b-row class="mt-3">
                              <b-col>
                                <h6>{{ $t('CommentGeneral') }}<span v-if="editMode"> (change on manuscript)</span></h6>
                                <span
                                  v-if="document.manuscript"
                                  v-html="document.manuscript.commentGeneral"
                                ></span>
                              </b-col>
                            </b-row>
                          </div>
                          <b-row class="mt-3">
                            <b-col>
                              <h6>{{ $t('Verso') }}</h6>
                              <b-form-input
                                id="sealProvider"
                                v-model="document.verso"
                                placeholder="Verso"
                                v-if="editMode"
                              ></b-form-input>
                              <span v-else v-html="document.verso"></span>
                            </b-col>
                          </b-row>
                        </b-col>
                      </b-row>
                      <b-row class="mt-3">
                        <b-col cols="8">
                          <h6>{{ $t('Regest') }}</h6>
                          <b-form-textarea
                            id="regest"
                            placeholder="Regest"
                            v-model="document.regest"
                            rows="3"
                            max-rows="6"
                            v-if="editMode"
                          ></b-form-textarea>
                          <span v-else v-html="document.regest"></span>
                        </b-col>
                      </b-row>
                    </b-col>

                    <b-col cols="8" class="mt-4">
                      <h6>{{ $t('Content') }}</h6>
                      <div>
                        <b-form-group label="Edit" class="small-title" v-if="editMode">
                          <b-form-radio-group
                            v-model="editViewType"
                            :options="editViewTypes"
                            name="edit-view-type-btn"
                            size="sm"
                            buttons
                          ></b-form-radio-group>
                        </b-form-group>
                        <b-form-group :label="$t('EditionViewBy')" class="small-title" v-else>
                          <b-form-radio-group
                            v-model="viewType"
                            :options="viewTypes"
                            name="view-type-btn"
                            size="sm"
                            buttons
                          ></b-form-radio-group>
                        </b-form-group>

                        <b-row class="justify-content-md-center">
                          <b-col cols="6">
                            <div v-if="!editMode">
                              <label class="switch">
                                <input type="checkbox" v-model="showHyperlinks">
                                {{ $t('ShowHyperlinks') }}
                              </label>
                            </div>

                            <div v-if="!editMode && this.viewType == 'critical'">
                              <label class="switch">
                                <input
                                  type="checkbox"
                                  v-model="showRhetorics"
                                >
                                {{ $t('DisplayRhetoricStructures')}}
                              </label>
                            </div>
                          </b-col>

                          <b-col cols="6">
                            <div>
                              <label
                                v-if="!editMode"
                                class="switch"
                              >
                                <input
                                  type="checkbox"
                                  v-model="pruneDiveLabels"
                                >
                                {{ $t('PruneDivLabels') }}
                              </label>
                            </div>

                            <div>
                              <label
                                v-if="dynamicImages && !editMode"
                                class="switch"
                              >
                                <input
                                  type="checkbox"
                                  v-model="disableDynamic"
                                >
                                {{ $t('DisableDynamicImages') }}
                              </label>
                            </div>
                          </b-col>
                        </b-row>
                      </div>

                      <div
                        id="content-box"
                        class="content-box"
                        @click="openContextMenu"
                        :class="showHyperlinks ? 'show-hyperlinks' : ''"
                      >
                        <div
                          id="content-div"
                          class="contentRoot edit-parent"
                          :class="viewType + ' ' + (editMode ? 'edit-' + editViewType : '')"
                        ></div>
                        <span v-if="footnotesFue.length && (!editMode || !(editViewType == 'par' || editViewType == 'zw'))" class="footnotes">
                          <small><b>{{ $t('TranscriptionNotes') }}</b></small><br>
                          <p
                            v-for="(footnote, index) in footnotesFue"
                            :key="'fue-' + index"
                            class="tooltip-box"
                            @click="centerFootnote(footnote.id)"
                            @mouseover="highlightId = footnote.id"
                            @mouseleave="highlightId = null"
                          >
                            <sup>{{ letterNumeration(index+1) }}</sup> <span v-html="footnote.content"></span>
                            <span class="contextmenu" v-if="editMode">
                              <button class="btn-legam-tooltip" @click="editFootnote(footnote)">
                                <b-icon icon="pencil"></b-icon>
                                Edit
                              </button>
                              <button class="btn-legam-tooltip" @click="deleteFootnote(footnote)">
                                <b-icon icon="trash"></b-icon>
                                Delete
                              </button>
                            </span>
                          </p>
                        </span>
                        <span v-if="footnotesFul.length && (!editMode || !(editViewType == 'par' || editViewType == 'zw'))" class="footnotes">
                          <small><b>{{ $t('LinguisticNotes') }}</b></small><br>
                          <p
                            v-for="(footnote, index) in footnotesFul"
                            :key="'ful-' + index"
                            class="tooltip-box"
                            @click="centerFootnote(footnote.id)"
                            @mouseover="highlightId = footnote.id"
                            @mouseleave="highlightId = null"
                          >
                            <sup>{{index+1}}</sup> <span v-html="footnote.content"></span>
                            <span class="contextmenu" v-if="editMode">
                              <button class="btn-legam-tooltip" @click="editFootnote(footnote)">
                                <b-icon icon="pencil"></b-icon>
                                Edit
                              </button>
                              <button class="btn-legam-tooltip" @click="deleteFootnote(footnote)">
                                <b-icon icon="trash"></b-icon>
                                Delete
                              </button>
                            </span>
                          </p>
                        </span>
                        <span v-if="footnotesFup.length && (!editMode || !(editViewType == 'par' || editViewType == 'zw'))" class="footnotes">
                          <small><b>{{ $t('ParallelismNotes') }}</b></small><br>
                          <p
                            v-for="(footnote, index) in footnotesFup"
                            :key="'fup-' + index"
                            class="tooltip-box"
                            @click="centerFootnote(footnote.id)"
                            @mouseover="highlightId = footnote.id"
                            @mouseleave="highlightId = null"
                          >
                            <sup>{{index+1}}</sup> <span v-html="footnote.content"></span>
                            <span class="contextmenu" v-if="editMode">
                              <button class="btn-legam-tooltip" @click="editFootnote(footnote)">
                                <b-icon icon="pencil"></b-icon>
                                Edit
                              </button>
                              <button class="btn-legam-tooltip" @click="deleteFootnote(footnote)">
                                <b-icon icon="trash"></b-icon>
                                Delete
                              </button>
                            </span>
                          </p>
                        </span>
                        <div
                          class="contextMenu"
                          v-if="showContextMenu"
                          @mouseleave="mouseLeave"
                          :style="'top:' + CMtop + 'px; left: ' + CMleft + 'px'"
                        >
                          <span v-if="!editMode">
                            <button class="btn-legam-tooltip" @click="filterDivision" v-if="config.editable">
                              Filter/Unfilter Tokens by division
                            </button>
                            <router-link
                              class="btn-legam-tooltip"
                              v-for="(tokenData, i) in contextMenuLemmas"
                              :key="`tokenContext-${i}`"
                              :to="{name: 'Lemma', params: {id: tokenData[0]}}"
                              target='_blank'
                              v-html="'Open Lemma «' + tokenData[1] + '»'"
                            ></router-link>
                            <br>
                          </span>
                        </div>
                      </div>
                    </b-col>
                    <b-col cols="4" class="img-col">
                      <template v-if="dynamicImages && !disableDynamic">
                        <img v-img:group
                          v-if="curImg"
                          :src="`${imagePath}/${curImg.file_path}`"
                          style="position: sticky; top: 120px;"
                          class="t-nail"
                        >
                        <div
                          v-else-if="curFol"
                          style="position: sticky; top: 120px;"
                        > {{ $t('NoImageFound') }} {{ this.curFol }}</div>
                      </template>
                      <template v-else>
                        <img v-img:group v-for="medium in document.media"
                          :key="medium.id"
                          :src="`${imagePath}/${medium.file_path}`"
                          class="t-nail"
                          style="top: 120px;"
                        >
                      </template>
                    </b-col>
                  </b-row>
                </b-col>
              </b-row>
            </article>
          </b-col>
        </b-row>

        <div class="accordion mt-4" role="tablist" v-if="config.editable">
          <b-card no-body class="mb-1">
            <b-card-header header-tag="header" class="p-1" role="tab">
              <b-button block v-b-toggle.accordion-1 class="text-left">
                Tokens
                <!-- Tokens <small>({{ document.tokens.length }} {{ document.tokens.length === 1 ? 'entry' : "entries" }})</small> -->
                <span class="when-open float-right">-</span>
                <span class="when-closed float-right">+</span>
              </b-button>
            </b-card-header>
            <b-collapse id="accordion-1" visible accordion="my-accordion-1" role="tabpanel">
              <b-card-body>
                <!-- <LegamDataTables v-bind="$data.tokensDataTables" /> -->
                <!-- <span v-if="editMode" class="btn-legam-xs btn-legam-xxs ml-2 mb-2 float-right" v-b-modal.modal-add-token>
                  <b-icon icon="plus"></b-icon>
                  Add
                </span>
                <b-modal id="modal-add-token" title="Add token" ok-only ok-variant="secondary" size="lg">
                  <TokenAutocomplete @select="addStudy" />
                </b-modal> -->
                <TokenTable
                  id="tokenTable"
                  ref="tokenTable"
                  v-if="documentTokens && config.editable"
                  :key="indexKeyTokenTable"
                  :tokens="documentTokens"
                  :documentId="documentId"
                  :editMode="editMode"
                  :copyTokenActions="copyTokenActions"
                  @highlight="highlightToken"
                  @refresh="reloadData"
                  @tokenJump="jumpToToken"
                  @removeDivFilter="filterDivision"
                  :divisionFilter="divisionFilter"
                />
              </b-card-body>
            </b-collapse>
          </b-card>
        </div>
      </b-container>
    </div>

    <b-modal
      id="modal-split-element"
      title="Edit element"
      ok-variant="secondary"
      size="xl"
      @ok="saveSplitElement"
      ok-title="OK"
    >
      <b-form-select
        id="edit-option"
        v-model="strelemEditOption"
        :options="strelemEditOptions"
      ></b-form-select>

      <template v-if="['split', 'fue', 'ful'].includes(strelemEditOption)">
        <div v-if="!['zw', 'fol'].includes(editViewType)" class="modal-data">
          <template v-for="(elem, i) in modalData">
            <span
              class="token"
              v-html="elem.innerHTML"
              :key="elem.idx"
            ></span>
            <template v-if="strelemEditOption == 'split'">
              <span
                v-if="i <= modalData.length - 2"
                class="token-ws"
                @click="selectSplit"
                :idx="parseInt(elem.getAttribute('idx')) + parseInt(elem.getAttribute('length')) + 1"
                :key="elem.idx"
              >&nbsp;</span>
            </template>
            <template v-else>
              <span
                class="token-ws"
                @click="selectSplit"
                :idx="parseInt(elem.getAttribute('idx')) + parseInt(elem.getAttribute('length')) + 1"
                :key="elem.idx"
              >&nbsp;</span>
            </template>
          </template>
        </div>
        <div v-else class="modal-data">
          <template v-for="(charList, i) in modalData">
            <template v-for="(char, j) in charList">
              <span
                v-if="char.class == 'token'"
                class="token"
                v-html="char.html"
                :key="`${i}x${j}`"
              ></span>
              <span
                v-else-if="j <= charList.length - 2"
                class="character-ws"
                @click="selectSplit"
                :idx="char.idx"
                :key="`${i}x${j}`"
              >&nbsp;</span>
            </template>
            <span
              v-if="i <= modalData.length - 2"
              class="token-ws"
              @click="selectSplit"
              :idx="charList[charList.length - 1].idx + 1"
              :key="i"
            >&nbsp;</span>
          </template>
        </div>
      </template>
      <template v-else-if="strelemEditOption == 'merge'">
        <div v-if="!thatElement">
          <span>Selected {{editViewType}}-Element has no following element - can't be merged</span>
        </div>
        <div v-else>
          <div>
            <span>Merge selected {{editViewType}}-Element</span>
          </div>
          <div class="to-merge" v-html="selectedStrelem.outerHTML"></div>
          <div>
            <span>into</span>
          </div>
          <div class="to-merge" v-html="thatElement.outerHTML"></div>
          <div>
            <span>(i.e. extend the first one to comprise the deleted second one)</span>
          </div>
        </div>
      </template>
      <template v-else-if="strelemEditOption == 'relabel'">
        <b-form-textarea
          id="textarea"
          v-model="newLabel"
          v-html="selectedStrelem ? selectedStrelem.children[0].innerHTML.trim() : ''"
          rows="1"
        ></b-form-textarea>
      </template>
      <template v-else-if="strelemEditOption == 'setDivType'">
        <b-form-checkbox
          v-model="selectedDivIsRubric"
        >
          divisio is rubric
        </b-form-checkbox>
      </template>
      <template v-else-if="strelemEditOption == 'alignDiv'">
        <b-form-select
          v-if="editViewType == 'div'"
          v-model="selectedVirtDiv"
          text-field="label"
          value-field="alignmentId"
          :options="virtDivs"
        >
        </b-form-select>
        <b-form-checkbox
          style="margin-top: 20px;"
          v-model="overwriteDivNumber"
        >
          overwrite local div-name with virtual div-name
        </b-form-checkbox>
      </template>
    </b-modal>

    <b-modal
      id="modal-edit-footnote"
      title="Edit footnote"
      ok-variant="secondary"
      size="xl"
      @ok="saveEditFootnote"
      @cancel="cancelEditFootnote"
      ok-title="Save"
    >
      <b-form-textarea
        id="textarea"
        v-model="footnoteText"
        placeholder="Footnote text"
        rows="3"
        max-rows="6"
      ></b-form-textarea>
    </b-modal>

    <b-modal
      id="modal-download-xml"
      title="Download XML"
      ok-variant="secondary"
      @ok="downloadXML"
      ok-title="Compile & Download"
    >
      <span>XML type</span>
      <b-form-checkbox
        switch
        v-model="downloadXml.type"
        :disabled="!downloadXml.target"
        class="mb-4 custom-switch">
        {{ downloadXml.type ? 'full' : 'light' }}
      </b-form-checkbox>

      <span>Target</span>
      <b-form-checkbox
        switch
        v-model="downloadXml.target"
        :disabled="downloadXml.type"
        class="mb-4 custom-switch">
        {{ downloadXml.target ? 'regular' : 'inDesign' }}
      </b-form-checkbox>

      <span>Punctuation</span>
      <b-form-select
        v-model="downloadXml.punctuation"
        :disabled="downloadXml.type"
        :options="punctuationOptions"
      ></b-form-select>

      <span>Numerals</span>
      <b-form-select
        v-model="downloadXml.numbers"
        :disabled="downloadXml.type"
        :options="numbersOptions"
      ></b-form-select>

    </b-modal>

    <b-modal
      id="modal-download-pdf"
      title="Download PDF"
      ok-variant="secondary"
      @ok="downloadPDF"
      ok-title="Compile & Download"
    >
      <span>Edition Type</span>
      <b-form-checkbox
        switch
        :disabled="downloadPdf.linenumbers"
        v-model="downloadPdf.isDiplomatic"
        class="mb-4 custom-switch">
        {{ ! downloadPdf.isDiplomatic ? 'critical' : 'diplomatic' }}
      </b-form-checkbox>

      <span>Rhetoric Structures</span>
      <b-form-checkbox
        switch
        :disabled="downloadPdf.isDiplomatic"
        v-model="downloadPdf.rhetorics"
        class="mb-4">
        {{ downloadPdf.rhetorics ? 'include rhetoric structures' : "don't include rhetoric structures" }}
      </b-form-checkbox>

      <span>Add Line Numbers</span>
      <b-form-checkbox
        switch
        :disabled="downloadPdf.isDiplomatic"
        v-model="downloadPdf.lineNumbers"
        class="mb-4">
      </b-form-checkbox>

      <span>Prune Division Labels</span>
      <b-form-checkbox
        switch
        v-model="downloadPdf.pruneDivs"
        class="mb-4">
      </b-form-checkbox>

      <span>Punctuation</span>
      <b-form-select
        v-model="downloadPdf.punctuation"
        :disabled="downloadPdf.isDiplomatic"
        :options="punctuationOptions"
      ></b-form-select>

      <span>Numerals</span>
      <b-form-select
        v-model="downloadPdf.numbers"
        :disabled="downloadPdf.type"
        :options="numbersOptions"
      ></b-form-select>

      <hr>

      <span>Target</span>
      <b-form-checkbox
        switch
        v-model="downloadPdf.toPdf"
        class="mb-4 custom-switch">
        {{ downloadPdf.toPdf ? 'PDF' : 'TeX' }}
      </b-form-checkbox>
    </b-modal>

    <component :is="`style`">
      sup[data-id="{{ highlightId }}"] {
        background-color: rgba(255, 0, 0, 0.7);
        color: #fff;
      }

      span[data-id="{{ highlightId }}"] {
        background-color: rgba(218, 112, 214, 0.4);
        border-radius: 5px;
      }
      .hlFol {
        background-color: thistle;
        border-radius: 5px;
      }
    </component>
  </div>
</template>

<style scoped>
  * >>> .tdAction {
    width: 70px;
  }

  * >>> .tdEdition {
    width: 70%;
  }

  * >>> .tdDateNum {
    width: 20%;
  }

  * >>> .show-hyperlinks .hl1 {
    /* background-color: greenyellow; */
    background-color: #1e9988;
    color: #fff;
  }

  * >>> .show-hyperlinks .hl2 {
    /* background-color: greenyellow; */
    background-color: #e7f5ff;
  }

  * >>> .bright {
    /* background-color: greenyellow; */
    background-color: gold;
    border-radius: 5px;
  }

  * >>> .rhetoric {
    font-size: smaller;
    opacity: 0.6;
    margin-bottom: 7px;
    margin-top: 12px;
    margin-left: 15px;
  }

  * >>> .invisible {
    display: none;
  }

  * >>> .content-box {
    font-size: 18px;
  }

  * >>> #modal-split-element #edit-option {
    margin-bottom: 20px;
    width: 20em;
  }

  * >>> maj {
    font-weight: bold;
  }

  * >>> abr {
    font-style: italic;
  }

  * >>> .modal-data .token {
    background-color: lightgoldenrodyellow;
    border-radius: 3px;
  }

  * >>> .modal-data .set-split {
    content: '✂';
    background-color: darkorchid;
  }

  * >>> .modal-data .character-ws.set-split {
    width: 1em;
    content: '✂';
    background-color: darkorchid;
  }

  * >>> .modal-data .token-ws {
    display: inline-block;
    width: 2em;
    border-radius: 3px;
  }

  * >>> .modal-data span.token-ws:hover {
    background-color: cornflowerblue;
  }

  * >>> .modal-data .character-ws {
    display: inline-block;
    width: 0.4em;
    border-radius: 3px;
  }

  * >>> .modal-data span.character-ws:hover {
    background-color: cornflowerblue;
    width: 1em;
  }

  .content-box p {
    margin-bottom: 2px;
  }

  * >>> .content-box .legam-divnum {
    font-size: 13px;
    opacity: 0.6;
    margin-right: 0.5em;
  }

  * >>> .content-box .diplomatic .legam-zw {
    margin-left: -32px;
    opacity: 0.6;
  }

  * >>> .content-box .space-before-div {
    display: block;
    margin-top: 20px;
    counter-increment: line-number;
    /* padding: 1px 2px 1px 2px; */
    background-color: #f3f3f3;
    margin-bottom: 4px;
    border-radius: 3px;
    margin-right: 7px;
  }

  * >>> .content-box .footnotes {
    display: block;
    margin-top: 13px;
  }

  * >>> .content-box .footnotes:before {
    content: "";
    display: block;
    width: 20%;
    padding-top: 10px;
    border-bottom: 1px solid rgb(162, 162, 162);
  }

  * >>> .content-box .edit-parent span.legam-zw,
  * >>> .content-box .edit-parent span.legam-div {
    min-width: 15px;
    padding-right: 5px;
    display: inline-block;
  }

  * >>> .content-box .edit-parent:not(.diplomatic):not(.edit-zw) span.legam-zw.inside {
    margin-right: -0.2em;
  }

  * >>> .content-box {
    counter-reset: line-number;
  }
  * >>> .content-box div.division {
    counter-increment: line-number;
    /* padding: 1px 2px 1px 2px; */
    background-color: #f3f3f3;
    margin-bottom: 4px;
    border-radius: 3px;
    display: inline;
    margin-right: 7px;
    /* line-height: 23px; */
  }

  .public-ui * >>> .content-box div.division {
    background-color: transparent;
  }

  * >>> .content-box .line-br {
    margin-bottom: 10px;
  }

  * >>> .to-merge {
    display: inline-block;
  }

  * >>> .edit-division {
    background-color: lightgoldenrodyellow;
    margin-top: 10px;
    margin-bottom: 10px;
  }

  * >>> .content-box .edit-strnum {
    margin-left: -2em;
    margin-right: 1em;
  }

  * >>> .content-box div.division:hover,
  * >>> .content-box div.space-before-div:hover,
  * >>> .content-box div.legam-par:hover,
  * >>> .content-box div.legam-line:hover {
    cursor: pointer;
    background-color: #e4e4e4;
  }
  * >>> .show-hyperlinks .content-box div:hover span.hl2 {
    background-color: #d5ebfb;
  }
  * >>> .content-box div > span.legam-div {
    vertical-align: super;
    font-size: 10px;
    font-weight: bold;
  }
  * >>> .content-box .edit-div div > span.legam-div {
    font-weight: normal;
    font-size: 10px;
    vertical-align: baseline;
    opacity: 0.6;
  }
  .contextMenu {
    background-color: #5a6268ed !important;
    position: fixed;
    padding: 10px 10px;
    z-index: 10000;
  }
  .legam-split-divider,
  .legam-split-divider-spaceholder {
    padding: 0px 3px;
    color: red;
    font-size: 30px;
  }
  .legam-split-divider-spaceholder {
    color: #dfdfdf;
  }
  .split-text {
    text-align: center;
    margin-bottom: 10px;
    font-size: 25px;
  }
  .text-center {
    text-align: center;
  }
  .text-center span {
    cursor: s-resize;
  }

  * >>> .content-box .disabled {
    opacity: 0.5;
  }
  .t-nail {
    width: 90%;
    margin-bottom: 20px;
  }
  .img-col {
    margin-top: 2cm;
  }

  * >>> .content-box .punct {
    font-family: "NotoSerif";
  }

  * >>> .content-box .rubric :is(.occ, .token) {
    font-variant: small-caps;
  }

  * >>> .content-box .rubric :is(.occ, .token) sup {
    font-variant: normal;
  }

  * >>> .content-box .isNotAligned {
    background-color: thistle;
  }

  /* no need to check for :not(:empty) since classes are added on the basis of specific innerHTML */
  * >>> .content-box .occ:has(+ .punct > punct:is(.noSpaceLeft, .noSpaceBoth)) {
    margin-right: -0.4em;
  }

  * >>> .content-box .punct:has(> punct.noSpaceBoth):has(+ .occ) {
    margin-right: -0.2em;
  }

  * >>> .content-box .punct:has(> punct.noSpaceRight):has(+ .occ) {
    margin-right: -0.2em;
  }

  * >>> .content-box punct:has(+ sup) {
    margin-right: -0.4em;
  }

  * >>> fua:before {
      content: "\00a0 ";
  }

  * >>> fua {
      color: var(--basic-color);
      font-size: small;
  }
  * >>> .custom-switch .custom-control-input:checked ~ .custom-control-label::before {
    background-color: #ccc !important; /* Change to your desired color */
    border-color: #aaa !important;
  }
</style>

<script>
  import { mapState } from 'vuex'
  import PageTitle from '@/components/PageTitle.vue'
  import router from '@/router'
  import Utils from '@/utils.js'
  import EditionSelect from '@/components/Bibliography/EditionSelect.vue'
  import ManuscriptSelect from '@/components/Bibliography/ManuscriptSelect.vue'
  import TextSelect from '@/components/Bibliography/TextSelect.vue'
  import TokenTable from '@/components/Document/TokenTable.vue'
  import config from '@/config.js'
  import { safeDownloadFilename } from '@/services/download.js'
  import { findLastOccurrenceToken } from '@/services/document-dom.js'
  import Vue from 'vue';
  import vImg from 'v-img';
  Vue.use(vImg);

  export default {
    name: 'DocumentDetails',
    data() {
      return {
        config: config,
        documentId: this.$route.params.id,
        editMode: false,
        loading: true,
        showDetails: false,
        documentTokens: null,
        indexKeyTokenTable: 0,
        elementNum: null,
        divisionFilter: null,
        showContextMenu: false,
        contextMenuLemmas: [],
        showHyperlinks: false,
        tokenLemmas: {},
        CMtop: 0,
        CMleft: 0,

        modalData: null,

        pruneDiveLabels: false,

        highlightId: null,

        footnoteText: '',
        footnoteId: null,
        newFootnoteId: null,
        footnotesFue: [],
        footnotesFul: [],
        footnotesFup: [],
        splitIdx: null,
        
        contentChanges: [],
        contentHTML: null,
        newLabel: null,

        downloadPdf: {
          mode: "individual",
          linenumbers: false,
          rhetorics: false,
          isDiplomatic: false,
          punctuation: "all",
          numbers: "original",
          lineNumbers: false,
          pruneDivs: false,
          toPdf: true
        },

        downloadXml: {
          type: false,
          target: true,
          punctuation: "all",
          numbers: "original"
        },

        punctuationOptions: ["modern", "original", "all"],
        numbersOptions: ["modern", "original"],

        romanNums: /^[IVCX]+$/,
        punctRegex: /<punct?( original="<?(?<original>.*)"){0,1}\/{0,1}>(?<modern>.){0,1}(<\/punct>){0,1}/i,
        numRegex: /<num?( original="<?(?<original>.*)"){0,1}\/{0,1}>(?<modern>.){0,1}(<\/num>){0,1}/i,

        viewType: 'critical',
        viewTypes: [
          { text: 'Critical', value: this.$t('critical') },
          { text: 'Interpretative', value: this.$t('interpretative') },
          { text: 'Diplomatic', value: this.$t('diplomatic') },
        ],
        editViewType: 'div',
        editViewTypes: [
          { text: 'Division', value: 'div' },
          { text: 'Paragraph', value: 'par' },
          { text: 'Line', value: 'zw' },
          { text: 'Folio', value: 'fol' },
        ],
        imagePath: config.imagesUrl,
        selectedImage: null,

        rhetorics: {
          cor: 'Corroboratio',
          dat: 'Datatio',
          disp: 'Dispositio',
          exp: 'Expositio',
          int: 'Intitulatio',
          inv: 'Invocatio',
          pub: 'Publicatio',
          sanct: 'Sanctio'
        },
        showRhetorics: false,

        checkbox: true,

        availableXMLs: [],
        copyTokenActions: {},

        tokenToHighlight: null,

        strelemEditOption: 'split',
        strelemEditOptionsFull: [
          {text: 'split', value: 'split'},
          {text: 'merge', value: 'merge'},
          {text: 'relabel', value: 'relabel'},
          {text: 'add footnote (transcription)', value: 'fue'},
          {text: 'add footnote (linguistic)', value: 'ful'},
          {text: 'change divisio type', value: 'setDivType'},
          {text: 'align division', value: 'alignDiv'},
          {text: 'delete', value: 'delete'}
        ],
        selectedStrelem: null,
        selectedVirtDiv: null,
        selectedDivIsRubric: null,
        overwriteDivNumber: false,

        virtDivs: [],
        dynamicImages: false,
        disableDynamic: false,
        curFol: null,
        curImg: null
      }
    },
    mounted() {
      this.tokenToHighlight = this.$route.query.tokenId
      this.reloadData()

      if (this.tokenToHighlight) this.viewType = 'diplomatic'
      if (this.document && this.documentTokens) this.computeContent()
    },
    computed: {
      ...mapState({
        document: state => state.document.document,
        tokens: state => state.token.tokens,
      }),
      thatElement() {
        return this.selectedStrelem?.nextElementSibling
      },
      strelemEditOptions() {
        if (this.editViewType === 'fol') {
          return this.strelemEditOptionsFull.slice(0, -5)
        } else if (this.editViewType != 'div') {
          return this.strelemEditOptionsFull.slice(0, -6)
        } else {
          if (this?.document?.parallelDocument) {
            return this.strelemEditOptionsFull.slice(0, -1)
          } else {
            return this.strelemEditOptionsFull.slice(0, -2)
          }
        }
      }
    },
    methods: {
      async centerItem(id, type, timeOut = 6000) {
        let htmlTag

        if (type === 'footnote') {
          htmlTag = 'sup'
        } else if (type === 'token') {
          htmlTag = 'span'
        }

        const item = document.querySelector(`${htmlTag}[data-id='${id}']`)
        item.scrollIntoView({block: 'center', behavior: 'smooth'})

        await new Promise(resolve => setTimeout(resolve, 500))
        this.highlightId = id
        await new Promise(resolve => setTimeout(resolve, timeOut))
        this.highlightId = null
      },
      jumpToToken(id) {
        this.centerItem(id, 'token', 10000)
      },
      nextFolio(entries) {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            let folio = entry.target.getAttribute("folio").match(/(\d+[rv])[abcd]?$/, '')

            if (folio) {
              folio = folio[1]
            } else {
              folio = entry.target.getAttribute("folio")
            }

            this.curFol = folio
            this.curImg = this.document.media.filter(x => x.title.match(folio))[0]

            Array.from(document.getElementsByClassName("hlFol")).forEach(x => {
              x.classList.remove("hlFol")
            })

            entry.target.classList.add("hlFol")
          }
        })
      },
      createObserver() {
        const options = {
          root: null,
          rootMargin: "-10% 0px -80% 0px",
          threshold: 0.0,
        }

        const observer = new IntersectionObserver(this.nextFolio, options)
        
        Array.from(document.getElementsByClassName("fol")).forEach(x => observer.observe(x))
      },
      centerFootnote(id) {
        this.centerItem(id, 'footnote')
      },
      oppositeAction(str) {
        return str == 'delete' ? 'create' : str == 'create' ? 'delete' : false
      },
      selectSplit(e) {
        this.splitIdx = e.target.getAttribute('idx')

        if (this.editViewType == 'zw') {
          Array.from(e.target.parentElement.parentElement.getElementsByClassName('character-ws')).forEach(x =>
            x.classList.remove('set-split')
          )
        }
        Array.from(e.target.parentElement.parentElement.getElementsByClassName('token-ws')).forEach(x =>
          x.classList.remove('set-split')
        )
        e.target.classList.add('set-split')
      },
      toggleRhetorics() {
        const rhets = document.getElementsByClassName('rhetoric')
        Array.from(rhets).forEach(x => x.classList.toggle('invisible'))
      },
      openDownloadPdf() {
        this.$bvModal.show('modal-download-pdf')
      },
      openDownloadXml() {
        this.$bvModal.show('modal-download-xml')
      },
      downloadXML() {
        this.$store.dispatch('getXML', {'docIds': [this.documentId], 'parameters': this.downloadXml}).then(data => {
          let filename = safeDownloadFilename(this.document.edition.sigleLegam, 'document', 'xml');
          let pom = document.createElement('a');
          let bb = new Blob([data], {type: 'application/xml'});
          pom.setAttribute('href', window.URL.createObjectURL(bb));
          pom.setAttribute('download', filename);
          pom.dataset.downloadurl = ['application/xml', pom.download, pom.href].join(':');
          pom.draggable = true;
          pom.classList.add('dragout');
          pom.click();
          window.setTimeout(() => window.URL.revokeObjectURL(pom.href), 0)
        }).catch(error => {
          this.$store.dispatch('addNotification', {
            type: 'danger',
            text: error.message || 'XML download failed',
          })
        })
      },
      downloadPDF() {
        this.$store.dispatch('getPDF', {'docIds': [this.documentId], 'parameters': this.downloadPdf}).then(data => {
          let filename = `${this.document.edition.sigleLegam}.${this.downloadPdf.toPdf ? 'pdf' : 'tex'}`;
          let pom = document.createElement('a');
          let bb = new Blob([data], {type: `application/${this.downloadPdf.toPdf ? 'pdf' : 'txt'}`});
          pom.setAttribute('href', window.URL.createObjectURL(bb));
          pom.setAttribute('download', filename);
          pom.dataset.downloadurl = [`application/${this.downloadPdf.toPdf ? 'pdf' : 'txt'}`, pom.download, pom.href].join(':');
          pom.draggable = true;
          pom.classList.add('dragout');
          pom.click();
        })
      },
      createElementFromHTML(htmlString) {
        let div = document.createElement('div');
        div.innerHTML = htmlString.trim();

        // Change this to div.childNodes to support multiple top-level nodes.
        return div.firstChild;
      },
      charactersToken(tokenDOM) {
        const idx = parseInt(tokenDOM.getAttribute('idx'))
        const len = parseInt(tokenDOM.getAttribute('length'))

        if (['punct', 'num'].includes(tokenDOM.getAttribute('type'))) {
          return [
            {idx: -1, class: 'token', html: tokenDOM.innerHTML.replace('&nbsp;', '').trim()},
            {idx: idx + len, class: 'character-ws', html: tokenDOM.innerHTML.replace('&nbsp;', '').trim()}
          ]
        }

        const charList = tokenDOM.innerHTML.replace('&nbsp;', '').trim().split('').map((x, i) => [x, i])

        let retList   = []
        let inTag     = false
        let openedTag = false
        let curTag    = ''

        charList.forEach(x => {
          const char = x[0]
          const cidx = x[1]

          if (char == '<') {
            inTag = true
            openedTag = openedTag ? false : true
          } else if (char == '>') {
            inTag = false
            if (!openedTag) {
              if (retList.length > 0) {
                retList[retList.length - 1].idx = retList[retList.length - 1].idx + curTag.length + 3
              }
              curTag = ''
            }
          } else if (inTag && openedTag){
            curTag += char
          } else if (inTag){
            return
          } else {
            if (curTag) {
              retList.push({idx: -1, class: 'token', html: `<${curTag}>${char}</${curTag}>`})
            } else {
              retList.push({idx: -1, class: 'token', html: char})
            }

            retList.push({idx: idx + cidx + 1, class: 'character-ws'})
          }
        })

        return retList
      },
      computePunctForm(DOMpunct) {
        if (DOMpunct) {
          const modernPunct = DOMpunct.innerHTML.trim()
          const origAttr    = DOMpunct.getAttribute('original')
          let origPunct
          let spacing

          if (["'", '’', '‘', '-', '·'].includes(modernPunct)) {
            spacing = 'noSpaceBoth'
          } else if ([',', '.', '…', ';', ':', '..', '...', '»', '”'].includes(modernPunct)) {
            spacing = 'noSpaceLeft'
          } else if (['«', '“'].includes(modernPunct)) {
            spacing = 'noSpaceRight'
          }

          if (origAttr) {
            origPunct = origAttr
                          .replace(/\./g, '·')
                          .replace(/:/g, '⹎')
                          .replace(/;/g, '⸵')
                          .replace(/,/g, '⸴')
                          .replace(/\//g, '⸍')
          }

          if (this.viewType == 'critical' || this.editMode == true) {
            if (origPunct && modernPunct) {
              DOMpunct.innerHTML = `${modernPunct}&nbsp;${origPunct} `
              DOMpunct.classList.add(spacing)
            } else if (origPunct) {
              DOMpunct.innerHTML = `${origPunct} `
            } else if (modernPunct) {
              DOMpunct.innerHTML = `${modernPunct} `
              DOMpunct.classList.add(spacing)
            }
          } else if (this.viewType == 'interpretative') {
            if (modernPunct) {
              DOMpunct.innerHTML = `${modernPunct} `
              DOMpunct.classList.add(spacing)
            } else {
              DOMpunct.innerHTML = ''
            }
          } else {
            origPunct ? DOMpunct.innerHTML = `${origPunct} ` : DOMpunct.innerHTML = ''
          }
        }
      },
      computeNumForm(DOMnum) {
        if (DOMnum) {
          const modernNum = DOMnum.innerHTML.trim()
          const origAttr  = DOMnum.getAttribute('original')
          let origNum

          if (origAttr) origNum = origAttr.replace(/\./g, '·')

          if (this.viewType == 'critical' || this.editMode == true) {
            if (origNum) {
              DOMnum.innerHTML = `${origNum} `
            } else if (modernNum) {
              DOMnum.innerHTML = `${modernNum} `
            }
          } else if (this.viewType == 'interpretative') {
            if (modernNum) {
              DOMnum.innerHTML = `${modernNum} `
            } else {
              DOMnum.innerHTML = ''
            }
          } else {
            origNum ? DOMnum.innerHTML = `${origNum} ` : DOMnum.innerHTML = ''
          }
        }
      },
      computeContentEdit() {
        function filterStrelems(strelems, modes) {
          let retStrelems = {}

          Object.entries(strelems).forEach(elem => {
            let matchElem = elem[1].filter(x => modes.includes(x.structuringelement.label))

            if (matchElem.length > 0) retStrelems[elem[0]] = matchElem
          })

          if (modes.length == 1 && (['fol', 'zw', 'par'].includes(modes[0]))) {
            if (!Object.keys(retStrelems).map(x => parseInt(x)).includes(0)) {
              retStrelems[0] = [{structuringelement: {label: modes[0]}}]
            }
          }

          return retStrelems
        }

        let elemNum = 0
        let modes
        let root = document.createElement('root')

        if (this.editViewType == 'rhetorics') {
          modes = this.rhetorics
        } else if (this.editViewType == 'div') {
          this.footnotesFue = []
          this.footnotesFul = []
          this.footnotesFup = []
          modes = ['div', 'fue', 'ful']
        } else {
          modes = [this.editViewType]
        }

        let strelems = filterStrelems(this.document.structuringelement, modes)

        function currStrelemIdx() {
          return Object.keys(strelems)[0]
        }

        let strelemIdx = currStrelemIdx()
        let strelem    = strelems[strelemIdx]
        let curStr     = root

        let fueCnt = 0
        let fulCnt = 0
        let fupCnt = 0

        // needed, 'this' is otherwise not in closure
        const that = this

        function addStrelemDOM(elem) {
          const label = elem.structuringelement.label

          if (['fue', 'ful', 'fup'].includes(label)) {
            let curChilds = curStr.getElementsByClassName('token')
            if (curChilds.length == 0) curChilds = curStr.previousElementSibling.getElementsByClassName('token')

            let lastToken = curChilds[curChilds.length - 1]

            lastToken.innerHTML = lastToken.innerHTML.trim()

            if (label == 'fue') {
              that.footnotesFue.push(elem)
              lastToken.innerHTML += `<sup data-id="${elem.id}">[${Utils.letterNumeration(++fueCnt)}]</sup> `
            } else if (label == 'ful') {
              that.footnotesFul.push(elem)
              lastToken.innerHTML += `<sup data-id="${elem.id}">[${++fulCnt}]</sup> `
            } else if (label == 'fup') {
              lastToken.innerHTML += `<sup data-id="${elem.id}">[${++fupCnt}]</sup> `
            }
          } else {
            let DOMelem = document.createElement('div')
            const id = `edit-div-${elem.id}`
            DOMelem.classList.add('edit-division')
            DOMelem.setAttribute('data-element-num', `${++elemNum}`)
            DOMelem.setAttribute('id', id)
            DOMelem.setAttribute('idx', elem.idx)
            DOMelem.setAttribute('strelem-type', label)
            if (elem.isRubricDiv) DOMelem.classList.add('rubric')
            if (that.document.parallelDocument && !elem.alignmentUnit && that.editViewType == 'div') DOMelem.classList.add('isNotAligned')

            DOMelem.addEventListener('click', (e) => {
              that.selectedStrelem     = e.target.closest('.edit-division')
              that.selectedDivIsRubric = that.selectedStrelem.classList.contains('rubric')
              that.selectedVirtDiv     = Object.values(that.document.structuringelement).flat().filter(x => {
                return x.id == that.selectedStrelem.id.split('-').reverse()[0]
              })[0]?.alignmentUnit?.alignmentId

              if (['zw', 'fol'].includes(that.editViewType)) {
                that.modalData = Array.from(e.target.closest('.edit-division').children).filter(x => 
                  x.classList.contains('token')
                ).map(x =>
                  that.charactersToken(x)
                )
              } else {
                that.modalData = Array.from(e.target.closest('.edit-division').children).filter(x => 
                  x.classList.contains('token')
                )
              }

              that.$bvModal.show('modal-split-element')
            })

            let divNum = document.createElement('span')
            divNum.classList.add('edit-strnum')
            divNum.classList.add('legam-divnum')
            if (!elem.content) {
              if (!(that.editViewType === 'fol')) {
                divNum.append(document.createTextNode(`${elemNum} `))
              } else {
                divNum.append(document.createTextNode(`- `))
              }
            } else {
              divNum.append(document.createTextNode(`${elem.content} `))
            }

            DOMelem.append(divNum)

            root.append(DOMelem)
            curStr = DOMelem
          }
        }

        this.documentTokens.forEach(token => {
          const tidx = token.idx
          const tlen = token.length

          let form    = token.formDoc
          let inToken = false
          let cut;

          while (strelemIdx <= tidx + token.length) {
            if (strelemIdx <= tidx) {
              strelem.forEach(x => addStrelemDOM(x))
            } else if (strelemIdx > tidx && strelemIdx <= tidx + token.length) {
              cut  = strelemIdx - tidx

              let pre  = form.slice(0, cut)
              let post = form.slice(cut)

              const tag = token.tokentype

              if (['punct', 'num'].includes(tag)) {
                pre  = pre + `</${tag}>`
                post = `<${tag}>` + post
              }

              let tokenDOM = this.createElementFromHTML(`<span idx="${tidx}" length="${tlen}" data-id="${token.id}">${pre}</span>`)
              tokenDOM.classList.add('token')
              tokenDOM.setAttribute('type', tag)
              curStr.append(tokenDOM)

              addStrelemDOM(strelem[0])

              tokenDOM = this.createElementFromHTML(`<span idx="${tidx}" length="${tlen}" data-id="${token.id}">${post}</span>`)
              tokenDOM.append(document.createTextNode(' '))
              tokenDOM.classList.add('token')
              tokenDOM.setAttribute('type', tag)
              curStr.append(tokenDOM)

              inToken = true
            }
            delete strelems[strelemIdx]
            strelemIdx = currStrelemIdx()
            strelem    = strelems[strelemIdx]
          }

          if (inToken) return

          let tokenDOM = this.createElementFromHTML(`<span idx="${tidx}" length="${tlen}" data-id="${token.id}">${form}</span>`)
          tokenDOM.append(document.createTextNode(' '))
          tokenDOM.classList.add('token')
          tokenDOM.setAttribute('type', token.tokentype)

          if (token.tokentype == 'punct') {
            let punctDOM = tokenDOM.querySelector('punct')
            this.computePunctForm(punctDOM)
            tokenDOM.innerHTML = ''
            tokenDOM.append(punctDOM)

            // make space after preceding token non-breaking
            if (curStr.children.length > 0) {
              let prevToken = curStr.children.item(curStr.children.length - 1)
              prevToken.innerHTML = prevToken.innerHTML.replace(/ $/, '&nbsp;')
            }
          }

          curStr.append(tokenDOM)
        })

        while (strelem) {
          strelem.forEach(x => addStrelemDOM(x))

          delete strelems[strelemIdx]
          strelemIdx = currStrelemIdx()
          strelem    = strelems[strelemIdx]
        }

        // this.contentHTML = root
        let contentDom = document.getElementById('content-div')
        contentDom.replaceChildren(root)
      },
      manDivDisplay(label) {
        if (this.pruneDiveLabels) {
          const parts = label.split('.').reverse()
          let outLabel = []

          for (const part of parts) {
            outLabel.push(part)

            if (!["I","i","1"].includes(part)) {
              break
            }
          }

          return outLabel.reverse().join(".")
        } else {
          return label
        }
      },
      computeContent() {
        // console.log("toks", this.documentTokens)
        // console.log("cont", this.document.content)
        // console.log("strelems", this.document.structuringelement)

        if (!this.documentTokens) return

        const bslash = '\\'

        let countDiv = 0
        let countZw  = 1
        let root     = document.createElement('root')
        let strelems = {...this.document.structuringelement}

        function currStrelemIdx() {
          return Object.keys(strelems)[0]
        }

        function outSideToken(that) {
          strelems[strelemIdx].forEach(elem => {
            const label = elem.structuringelement.label

            if (label == 'div') {
              let DOMelem = document.createElement(label)
              DOMelem.classList.add('division')
              if (elem.isRubricDiv) DOMelem.classList.add('rubric')
              DOMelem.setAttribute('data-element-num', `${++countDiv}`)

              if (that.viewType == 'diplomatic' && strelemIdx == 0) {
                let zwElem = document.createElement('span')
                zwElem.classList.add('legam-zw')
                zwElem.classList.add('legam-divnum')
                zwElem.append(document.createTextNode(`${bslash}1 `))

                let spanDOM = document.createElement('span')

                spanDOM.classList.add('legam-line')
                spanDOM.setAttribute('data-id', elem.id)

                spanDOM.append(zwElem)
                DOMelem.append(spanDOM)
              }

              let divNum = document.createElement('span')
              divNum.classList.add('legam-divnum')

              if (!elem.content) {
                divNum.append(document.createTextNode(countDiv))
              } else {
                if (!elem.isRubricDiv) {
                  divNum.append(document.createTextNode(that.manDivDisplay(elem.content)))
                } else {
                  divNum.append(document.createTextNode(elem.content))
                }
              }

              divNum.append(document.createTextNode(' '))
              DOMelem.append(divNum)
              
              root.append(DOMelem)
              curStr = DOMelem
            } else if (label == 'par') {
              let DOMelem = document.createElement('span')
              DOMelem.classList.add('legam-par')

              if (that.viewType == 'critical' | that.viewType == 'interpretative') {
                DOMelem.append(document.createElement('br'))
              }

              curStr.append(DOMelem)
            } else if (label == 'zw') {
              let zwElem = document.createElement('span')
              zwElem.classList.add('legam-zw')
              zwElem.classList.add('legam-divnum')
              zwElem.append(document.createTextNode(`${bslash}${++countZw} `))

              if (that.viewType == 'diplomatic') {
                let spanDOM = document.createElement('span')
                let lineBr  = document.createElement('br')

                spanDOM.classList.add('legam-line')
                spanDOM.setAttribute('data-id', elem.id)

                lineBr.classList.add('line-br')

                spanDOM.append(lineBr)
                spanDOM.append(zwElem)
                curStr.append(spanDOM)
              } else {
                curStr.append(zwElem)
              }
            } else if (label == 'fol') {
              const type = elem.folType
              let DOMelem

              if (type == "manuscript") {
                DOMelem = that.createElementFromHTML(`<sub style="opacity: 0.6;">{${elem.content}} </sub>`)
                DOMelem.classList.add('fol')
                DOMelem.setAttribute('folio', elem.content)
              } else {
                DOMelem = that.createElementFromHTML(`<sub style="opacity: 0.6;">[${elem.content}] </sub>`)
                DOMelem.classList.add('modern-fol')
                DOMelem.setAttribute('folio', elem.content)
              }

              curStr.append(DOMelem)
            } else if (['fue', 'ful', 'fup'].includes(label)) {
              const lastToken = findLastOccurrenceToken(curStr)

              if (!lastToken) {
                throw new Error(
                  `Document ${that.documentId}: ${label} ${elem.id} at index ` +
                  `${strelemIdx} has no preceding occurrence token ` +
                  `(${that.documentTokens.length} document tokens)`
                )
              }

              lastToken.innerHTML = lastToken.innerHTML.replace(/(?:&nbsp;|\s)$/, '')

              if (label == 'fue') {
                that.footnotesFue.push(elem)
                lastToken.innerHTML += `<sup title="${elem.content}" data-id="${elem.id}">[${Utils.letterNumeration(that.footnotesFue.length)}]</sup> `
              } else if (label == 'ful') {
                that.footnotesFul.push(elem)
                lastToken.innerHTML += `<sup title="${elem.content}" data-id="${elem.id}">[${that.footnotesFul.length}]</sup>`
                lastToken.append(document.createTextNode(' '))
              } else if (label == 'fup') {
                that.footnotesFup.push(elem)
                lastToken.innerHTML += `<sup title="${elem.content}" data-id="${elem.id}">∥${that.footnotesFup.length}∥</sup>`
                lastToken.append(document.createTextNode(' '))
              }
            } else if (Object.keys(that.rhetorics).includes(label) && that.viewType == 'critical') {
              let elemDOM = document.createElement('div')
              elemDOM.classList.add('rhetoric')
              if (!that.showRhetorics) elemDOM.classList.add('invisible')
              elemDOM.innerHTML = `<span><sc>${that.rhetorics[label]}</sc></span>`
              root.append(elemDOM)
            }
          })
        }

        let strelemIdx = currStrelemIdx()
        let curStr     = root

        this.footnotesFue = []
        this.footnotesFul = []

        this.documentTokens.forEach(token => {
          const tidx = token.idx

          let form  = token.formDoc
          let shift = 0

          while (strelemIdx <= tidx + token.length) {
            if (strelemIdx <= tidx) {
              outSideToken(this)
            } else if (strelemIdx > tidx && strelemIdx <= tidx + token.length) {
              strelems[strelemIdx].forEach(elem => {
                const label = elem.structuringelement.label
                const cut   = (elem.idx - tidx) + shift
                let   interStr = ''

                if (label == "zw") {
                  interStr = `<span class="legam-zw legam-divnum inside">${bslash}${++countZw}</span>`

                  if ((this.editMode == true && this.editViewType == 'zw') || (this.editMode == false && this.viewType == 'diplomatic')) {
                    interStr = `<span class="legam-line" data-id="${elem.id}"><br class="line-br">${interStr}</span>`
                  }
                } else if (label == 'fol') {
                    const type = elem.folType
                    if (type == "manuscript") {
                      interStr = `<sub class="fol" folio="${elem.content}" style="opacity: 0.6;">{${elem.content}}</sub>`
                    } else {
                      interStr = `<sub class="modern-fol" folio="${elem.content}" style="opacity: 0.6;">[${elem.content}]</sub>`
                    }
                } else if (['fue', 'ful', 'fup'].includes(label)) {
                  if (label == 'fue') {
                    this.footnotesFue.push(elem)
                    interStr = `<sup title="${elem.content}" data-id="${elem.id}">[${Utils.letterNumeration(this.footnotesFue.length)}]</sup>`
                  } else if (label == 'ful') {
                    this.footnotesFul.push(elem)
                    interStr = `<sup title="${elem.content}" data-id="${elem.id}">[${this.footnotesFul.length}]</sup>`
                  } else if (label == 'fup') {
                    this.footnotesFup.push(elem)
                    interStr = `<sup title="${elem.content}" data-id="${elem.id}">∥${this.footnotesFup.length}∥</sup>`
                  }
                }
                form   = form.slice(0, cut) + interStr + form.slice(cut)
                shift += interStr.length
              })
            }
            delete strelems[strelemIdx]
            strelemIdx = currStrelemIdx()
          }

          let tokenDOM = this.createElementFromHTML(`<span data-id="${token.id}">${form} </span>`)

          if (token.tokentype == 'punct') {
            let punctDOM = tokenDOM.querySelector('punct')
            this.computePunctForm(punctDOM)
            tokenDOM.innerHTML = ''
            tokenDOM.append(punctDOM)
            tokenDOM.classList.add('punct')

            // make space after preceding token non-breaking
            if (curStr.children.length > 0) {
              let prevToken = curStr.children.item(curStr.children.length - 1)
              prevToken.innerHTML = prevToken.innerHTML.replace(/ $/, '&nbsp;')
            }
          } else if (token.tokentype == 'num') {
            let punctDOM = tokenDOM.querySelector('num')
            this.computeNumForm(punctDOM)
            tokenDOM.innerHTML = ''
            tokenDOM.append(punctDOM)
            tokenDOM.classList.add('num')
          } else if (token.tokentype == 'occ') {
            tokenDOM.classList.add('occ')
          }

          if (token.lemmatized) {
            tokenDOM.classList.add('hl2')
            this.tokenLemmas[token.id] = token.lemmas
          }

          if (token.id == this.tokenToHighlight) tokenDOM.classList.add('bright')

          curStr.append(tokenDOM)
        })

        while (Object.keys(strelems).length > 0) {
          outSideToken(this)

          delete strelems[strelemIdx]
          strelemIdx = currStrelemIdx()
        }

        let contentDom = document.getElementById('content-div')
        contentDom.replaceChildren(root)

        if (this.tokenToHighlight) document.querySelector(`span[data-id='${this.tokenToHighlight}']`).scrollIntoView({block: "center", behavior: 'smooth'})
        if (this.dynamicImages) this.createObserver()
      },
      letterNumeration: Utils.letterNumeration,
      mouseLeave() {
        this.showContextMenu = false
      },
      async reloadData() {
        this.documentTokens   = []
        this.copyTokenActions = {}

        this.loading = true
        try {
          await this.$store.dispatch('loadDocument', {
            documentId: this.documentId,
          })
          await this.$store.dispatch('loadTokensForDocument', {
            documentId: this.documentId,
            document: this.document,
          })

          if (this.document.parallelDocument) {
            try {
              const data = await this.$store.dispatch(
                'getVirtualDivs',
                this.document.parallelDocument
              )
              this.virtDivs = data.data.concat('-- (disalign)')
            } catch (error) {
              this.$store.dispatch('addNotification', {
                type: 'warning',
                text: error.message || 'Parallel divisions could not be loaded',
              })
            }
          }
          this.dynamicImages = Object.values(this.document.structuringelement)
            .flat()
            .some(x => x.structuringelement.label == 'fol')
        } catch (error) {
          this.$store.dispatch('addNotification', {
            type: 'danger',
            text: error.message || 'Document data could not be loaded',
          })
        } finally {
          this.loading = false
        }
      },
      openContextMenu(event) {
        let elem = null
        let className = 'division'

        if (event.target.classList.contains(className)){
          elem = event.target
        }
        else {
          elem = event.target.parentNode.closest(`.${className}`)
        }

        if (elem) {
          this.CMleft = event.clientX
          this.CMtop = event.clientY
          this.showContextMenu = true
          this.elementNum = parseInt(elem.dataset.elementNum, 10)
          this.contextMenuLemmas = event.target.dataset.id in this.tokenLemmas ? this.tokenLemmas[event.target.dataset.id] : []
        }
      },

      // Split element
      saveSplitElement() {
        if (this.strelemEditOption == 'split' && this.splitIdx) {
          const action   = 'create'
          const pseudoId = 'pID' + Date.now()

          if (this.document.structuringelement[this.splitIdx]) {
            this.document.structuringelement[this.splitIdx].push({id: pseudoId, idx: this.splitIdx, structuringelement: {label: this.editViewType}})
          } else {
            this.document.structuringelement[this.splitIdx] = [{id: pseudoId, idx: this.splitIdx, structuringelement: {label: this.editViewType}}]
          }

          if (this.contentChanges.find(x => x.index == this.splitIdx && x.type == this.editViewType && x.action == this.oppositeAction(action))) {
            this.contentChanges = this.contentChanges.filter(x => !(x.index == this.splitIdx && x.type == this.editViewType && x.action == this.oppositeAction(action)))
          } else {
            this.contentChanges.push({
              type: this.editViewType,
              action: action,
              index: this.splitIdx,
              pseudoId: pseudoId
            })
          }
        } else if (this.strelemEditOption == 'merge') {
          const action  = 'delete'
          const type    = this.selectedStrelem.getAttribute('strelem-type')
          const numElem = parseInt(Array.from(this.selectedStrelem.children)
                            .find(x => x.classList.contains('edit-strnum'))
                            .parentElement
                            .getAttribute('data-element-num'))
          let elem

          if (type == 'div') {
            elem = Object.entries(this.document.structuringelement).filter(x => x[1].some(y => y.structuringelement.label == type))[numElem][1][0]
          } else {
            elem = Object.entries(this.document.structuringelement).filter(x => x[1].some(y => y.structuringelement.label == type))[numElem - 1][1][0]
          }

          this.document.structuringelement[elem.idx] = this.document.structuringelement[elem.idx].filter(x => x.id !== elem.id)
          
          if (this.contentChanges.find(x => x.index == elem.idx && x.type == type && x.action == this.oppositeAction(action))) {
            this.contentChanges = this.contentChanges.filter(x => !(x.index == elem.idx && x.type == type && x.action == this.oppositeAction(action)))
          } else {
            this.contentChanges.push({
              type: type,
              action: action,
              id: elem.id,
              index: elem.idx
            })
          }
        } else if (this.strelemEditOption == 'relabel') {
          const id   = this.selectedStrelem.getAttribute('id').split('-').reverse()[0]

          if (!(id === 'undefined')) {
            Object.entries(this.document.structuringelement).forEach(entry => {
              this.document.structuringelement[entry[0]] = entry[1].map(x => {
                if (x.id == id) {
                  return {...x, content: this.newLabel}
                } else {
                  return x
                }
              })
            })

            if (id.startsWith('pID')) {
              const idx = this.contentChanges.findIndex(x => x.pseudoId == id)

              this.contentChanges = this.contentChanges.slice(0, idx).concat(
                {...this.contentChanges[idx], content: this.newLabel}
              ).concat(this.contentChanges.slice(idx + 1))
            } else {
              this.contentChanges.push({
                type: this.editViewType,
                action: 'update-content',
                id: id,
                content: this.newLabel
              })
            }
          } else {
            // this means we are editing the first pseudo-element (zw, fol): if there is no element at idx 0
            // it is virtually created in the editing interface. this detected bc. the id is the string (!) 'undefined'

            const newElem = {
              index: 0,
              content: this.newLabel,
              structuringelement: {
                label: this.editViewType
              }
            }

            if (this.document.structuringelement[0]) {
              this.document.structuringelement[0].push(newElem)
            } else {
              this.document.structuringelement[0] = newElem
            }

            this.contentChanges.push({
              type: this.editViewType,
              action: 'create',
              index: 0,
              content: this.newLabel
            })
          }

        } else if (this.strelemEditOption == 'setDivType') {
          const sidx = this.selectedStrelem.getAttribute('idx')
          const id   = this.selectedStrelem.getAttribute('id').split('-').reverse()[0]

          this.document.structuringelement[sidx] = this.document.structuringelement[sidx].map(x => {
            if (x.id == id) {
              return {...x, isRubricDiv: this.selectedDivIsRubric}
            } else {
              return x
            }
          })

          if (id.startsWith('pID')) {
            const idx = this.contentChanges.findIndex(x => x.pseudoId == id)

            this.contentChanges = this.contentChanges.slice(0, idx).concat(
              {...this.contentChanges[idx], divType: this.selectedDivIsRubric}
            ).concat(this.contentChanges.slice(idx + 1))
          } else {
            this.contentChanges.push({
              type: this.editViewType,
              action: 'change-div-type',
              id: id,
              divType: this.selectedDivIsRubric
            })
          }
        } else if (this.strelemEditOption == 'alignDiv') {
          const selVirtDiv = this.selectedVirtDiv == '-- (disalign)' ? null : this.virtDivs.find(x => x.alignmentId === this.selectedVirtDiv)
          const sidx       = this.selectedStrelem.getAttribute('idx')
          const id         = this.selectedStrelem.getAttribute('id').split('-').reverse()[0]

          this.document.structuringelement[sidx] = this.document.structuringelement[sidx].map(x => {
            if (x.id == id) {
              return {
                ...x,
                alignmentUnit: selVirtDiv,
                content: this.overwriteDivNumber ? selVirtDiv.label : x.content
              }
            } else {
              return x
            }
          })

          if (id.startsWith('pID')) {
            const idx = this.contentChanges.findIndex(x => x.pseudoId == id)

            if (this.overwriteDivNumber) {
              this.contentChanges = this.contentChanges.slice(0, idx).concat(
                {
                  ...this.contentChanges[idx],
                  alignmentUnit: selVirtDiv.alignmentId,
                  content: selVirtDiv.label
                }
              ).concat(this.contentChanges.slice(idx + 1))
            } else {
              this.contentChanges = this.contentChanges.slice(0, idx).concat(
                {
                  ...this.contentChanges[idx],
                  alignmentUnit: selVirtDiv.alignmentId
                }
              ).concat(this.contentChanges.slice(idx + 1))
            }
          } else {
            if (this.overwriteDivNumber) {
              this.contentChanges.push({
                type: this.editViewType,
                action: 'change-align-unit',
                id: id,
                alignmentUnit: selVirtDiv.alignmentId,
                content: selVirtDiv.label
              })
            } else {
              this.contentChanges.push({
                type: this.editViewType,
                action: 'change-align-unit',
                id: id,
                alignmentUnit: selVirtDiv.alignmentId
              })
            }
          }
        } else if (['fue', 'ful'].includes(this.strelemEditOption)) {
          this.newFootnoteId = Date.now()

          const newObj = {
            idx: this.splitIdx,
            id: this.newFootnoteId,
            structuringelement: {
              label: this.strelemEditOption
            }
          }

          if (this.document.structuringelement[this.splitIdx]) {
            this.document.structuringelement[this.splitIdx].push(newObj)
          } else {
            this.document.structuringelement[this.splitIdx] = [newObj]
          }

          this.$bvModal.show('modal-edit-footnote')

        }

        this.splitIdx = null
        this.selectedDivIsRubric = null
        this.selectedStrelem = null
        this.selectedVirtDiv = null

        this.computeContentEdit()
      },

      // Footnotes
      editFootnote(footnote) {
        this.footnoteId = footnote.id
        this.footnoteText = footnote.content
        this.$bvModal.show('modal-edit-footnote')
      },
      cancelEditFootnote() {
        this.document.structuringelement[this.splitIdx] = this.document.structuringelement[this.splitIdx].filter(
          x => x.id != this.newFootnoteId
        )
        this.footnoteText = ''
        this.footnoteId = null
        this.computeContentEdit()
      },
      saveEditFootnote() {
        if (this.footnoteId) {
          Object.values(this.document.structuringelement).forEach(x => {
            x.forEach(y => {
              if (y.id == this.footnoteId) y.content = this.footnoteText
            })
          })

          this.contentChanges.push({
            type: 'footnote',
            action: 'update-content',
            id: this.footnoteId,
            content: this.footnoteText
          })
        } else {
          let newIdx

          Object.entries(this.document.structuringelement).forEach(x => {
            for (const elem of x[1]) {
              if (elem.id == this.newFootnoteId) {
                elem.content = this.footnoteText
                newIdx       = x[0]
                break
              }
            }
          })

          this.contentChanges.push({
            type: this.strelemEditOption,
            action: 'create',
            content: this.footnoteText,
            index: newIdx,
          })
        }

        this.footnoteText = ''
        this.footnoteId = null
        this.computeContentEdit()
      },
      deleteFootnote(footnote) {
        this.$bvModal.msgBoxConfirm('Are you sure?').then(value => {
          if (value){
            if (footnote.structuringelement.label == 'fue') {
              this.footnotesFue = this.footnotesFue.filter(x => x.id != footnote.id)
            } else if (footnote.structuringelement.label == 'ful') {
              this.footnotesFul = this.footnotesFul.filter(x => x.id != footnote.id)
            } else if (footnote.structuringelement.label == 'fup') {
              this.footnotesFup = this.footnotesFup.filter(x => x.id != footnote.id)
            }

            this.document.structuringelement[footnote.idx] = this.document.structuringelement[footnote.idx].filter(
              x => x.id != footnote.id
            )

            if (this.contentChanges.find(x => x.id == footnote.id)) {
              this.contentChanges = this.contentChanges.filter(x => x.id != footnote.id)
            } else {
              this.contentChanges.push({
                type: footnote.structuringelement.label,
                action: 'delete',
                id: footnote.id,
                index: footnote.idx
              })
            }

            this.computeContentEdit()
          }
        })
      },

      unlockEdit() {
        // this.editMode = true
        this.$store.dispatch('lockDocument', {
          documentId: this.documentId,
          status: 'lock'
        }).then(data => {
          if (data.status == 'ok') {
            this.editMode = true
            this.computeContentEdit()
          }
          else if (data.status == 'false'){
            this.$store.dispatch('addNotification', {
              type: 'warning',
              text: `Document is locked by ${data.username}`
            })
          }
        })
      },
      filterDivision() {
        let message;

        this.copyTokenActions = this.$refs.tokenTable.tokenActions

        if (this.divisionFilter === this.elementNum) {
          this.divisionFilter = null
          message = 'Division Filter removed'
        } else {
          const manDiv        = this.tokens.find(x => x.division == this.elementNum).content
          this.divisionFilter = this.elementNum
          message             = `Tokens filtered by division ${manDiv || this.divisionFilter}`
          document.querySelector("#tokenTable").scrollIntoView({behavior: 'smooth'});
        }
        this.indexKeyTokenTable++
        this.$store.dispatch('addNotification', {
          type: 'success',
          text: message
        })
        this.showContextMenu = false
      },
      highlightToken(data){
        this.documentTokens = this.tokens.map(token => {
          token.highlight = false
          if (token.id == data.id){
            token.highlight = true
          }
          return token
        })
        window.scrollTo(20, top);
      },
      selectEdition(edition){
        this.document.editionId = null
        this.document.edition = null
        if (edition) {
          this.document.editionId= edition.id
          this.document.edition = edition
        }
      },
      selectCorpusEdition(edition){
        this.document.corpus.editionId = null
        this.document.corpus.edition = null
        if (edition) {
          this.document.corpus.editionId= edition.id
          this.document.corpus.edition = edition
        }
      },
      selectManuscript(manuscript){
        this.document.manuscriptId = null
        this.document.manuscript = null
        if (manuscript){
          this.document.manuscriptId= manuscript.id
          this.document.manuscript = manuscript
        }
      },
      selectText(text){
        this.document.textId = null
        this.document.text = null
        if (text) {
          this.document.textId= text.id
          this.document.text = text
        }
      },
      selectCorpus(corpus){
        this.document.corpusId = null
        this.document.corpus = null
        if (corpus){
          this.document.corpusId = corpus.id
          this.document.corpus = corpus
        }
      },
      cancelEdit(){
        this.$store.dispatch('lockDocument', {
          documentId: this.documentId,
          status: 'unlock'
        }).then(data => {
          if (data.status == 'ok') {
            this.editMode = false
            this.contentChanges = []
            this.computeContent()
            this.reloadData()
          }
        })
      },
      saveEdit(){
        this.editMode = false
        this.$store.dispatch('saveDocument', {
          document: this.document,
          changes: this.contentChanges
        }).then(() => {
          this.contentChanges = []
          this.reloadData()
          this.$store.dispatch('addNotification', {
            type: 'success',
            text: `Changes saved`
          })
        })
      },
      deleteItem(){
        this.$bvModal.msgBoxConfirm('Are you sure?').then(value => {
          if (value){
            this.$store.dispatch('deleteDocument', this.document).then(() => {
              router.push('/documents')
            })
          }
        })
      },
      scriptoriumString: Utils.scriptoriumString,
    },
    components: {
      PageTitle,
      EditionSelect,
      ManuscriptSelect,
      TokenTable,
      TextSelect,
    },
    watch: {
      pruneDiveLabels() {
        this.computeContent()
      },
      'showRhetorics'() {
        this.toggleRhetorics()
      },
      'documentId'(){
        this.reloadData()
      },
      'document'(){
        this.loading = false

        this.$nextTick(() => {
          if (this.document && this.documentTokens.length > 0 && document.getElementById('content-div')) {
            if (!this.editMode) {
              this.computeContent()
            } else {
              this.computeContentEdit()
            }
          }
        })
      },
      'viewType'(){
        this.computeContent()
      },
      'editViewType'(){
        this.modalData = null
        this.strelemEditOption = 'split'
        this.computeContentEdit()
      },
      'downloadPdf.isDiplomatic'(){
        if (this.downloadPdf.isDiplomatic) {
          this.downloadPdf.punctuation = "original"
          // deselect rhetoric structures if selected
        } else {
          this.downloadPdf.punctuation = "all"
        }
      },
      'tokens'(){
        this.indexKeyTokenTable++
        this.documentTokens = this.tokens

        this.$nextTick(() => {
          if (this.document && this.documentTokens && document.getElementById('content-div')) {
            this.computeContent()
          }
        })
      },
    },
  }
</script>
